import { useEffect, useMemo, useState } from 'react'

import {
  fetchPublicMarketVendors,
  type PublicMarketVendor,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { fetchPublishedEvents, type CalendarEvent } from '@/api/eventsApi'

import styles from './MarketCalendar.module.css'

const WEEKDAYS = [
  { short: 'S', label: 'Sunday' },
  { short: 'M', label: 'Monday' },
  { short: 'T', label: 'Tuesday' },
  { short: 'W', label: 'Wednesday' },
  { short: 'T', label: 'Thursday' },
  { short: 'F', label: 'Friday' },
  { short: 'S', label: 'Saturday' },
] as const

const MAX_VISIBLE_DOTS = 2

function toIsoDate(value: Date): string {
  const y = value.getFullYear()
  const m = String(value.getMonth() + 1).padStart(2, '0')
  const d = String(value.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const cursor = new Date(year, month + delta, 1)
  return { year: cursor.getFullYear(), month: cursor.getMonth() }
}

function monthLabel(year: number, month: number): string {
  return new Intl.DateTimeFormat('en-ZA', { month: 'long', year: 'numeric' }).format(
    new Date(year, month, 1),
  )
}

function formatDayHeading(iso: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T12:00:00`))
}

function formatTime(value: string | null | undefined): string | null {
  if (!value) return null
  return value.slice(0, 5)
}

function eventCoversDate(event: CalendarEvent, iso: string): boolean {
  const end = event.end_date || event.event_date
  return iso >= event.event_date && iso <= end
}

function sortDayEvents(dayEvents: CalendarEvent[]): CalendarEvent[] {
  return [...dayEvents].sort((a, b) => {
    const aTime = a.start_time ?? '99:99:99'
    const bTime = b.start_time ?? '99:99:99'
    if (aTime !== bTime) return aTime.localeCompare(bTime)
    if (a.event_type !== b.event_type) return a.event_type.localeCompare(b.event_type)
    return a.title.localeCompare(b.title)
  })
}

function typeClass(eventType: CalendarEvent['event_type']): string {
  if (eventType === 'market') return styles.dotMarket ?? 'dotMarket'
  if (eventType === 'town') return styles.dotTown ?? 'dotTown'
  return styles.dotOther ?? 'dotOther'
}

function typeLabel(eventType: CalendarEvent['event_type']): string {
  if (eventType === 'market') return 'Market day'
  if (eventType === 'town') return 'Town event'
  return 'Other'
}

function buildCells(year: number, month: number): Array<{ date: Date; iso: string; inMonth: boolean }> {
  const first = new Date(year, month, 1)
  const sundayOffset = first.getDay() // 0 = Sunday
  const gridStart = new Date(year, month, 1 - sundayOffset)
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index)
    return {
      date,
      iso: toIsoDate(date),
      inMonth: date.getMonth() === month,
    }
  })
}

type VendorsPopupState = {
  event: CalendarEvent
  title: string
  loading: boolean
  error: string
  marketTitle: string | null
  vendors: PublicMarketVendor[]
}

/**
 * Flyer-styled month calendar for market days and town events.
 * Supports multiple events on the same day (dots + count, full list below).
 */
export function MarketCalendar() {
  const todayIso = toIsoDate(new Date())
  const [cursor, setCursor] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [selectedIso, setSelectedIso] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [vendorsPopup, setVendorsPopup] = useState<VendorsPopupState | null>(null)

  const cells = useMemo(
    () => buildCells(cursor.year, cursor.month),
    [cursor.year, cursor.month],
  )
  const from = cells[0]?.iso
  const to = cells[cells.length - 1]?.iso

  useEffect(() => {
    if (!from || !to) return
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError('')
      try {
        const result = await fetchPublishedEvents({ from, to })
        if (cancelled) return
        setEvents(result.events)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiClientError ? err.message : 'Could not load calendar events.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [from, to])

  useEffect(() => {
    const firstInMonth = cells.find(
      (cell) => cell.inMonth && events.some((event) => eventCoversDate(event, cell.iso)),
    )
    setSelectedIso(firstInMonth?.iso ?? null)
  }, [cells, events])

  useEffect(() => {
    if (!vendorsPopup) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setVendorsPopup(null)
    }
    window.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [vendorsPopup])

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const cell of cells) {
      const dayEvents = sortDayEvents(
        events.filter((event) => eventCoversDate(event, cell.iso)),
      )
      if (dayEvents.length) map.set(cell.iso, dayEvents)
    }
    return map
  }, [cells, events])

  const selectedEvents = selectedIso ? (eventsByDay.get(selectedIso) ?? []) : []

  function shiftMonth(delta: number) {
    setCursor((current) => addMonths(current.year, current.month, delta))
  }

  async function openVendorsPopup(event: CalendarEvent) {
    const date = event.event_date.slice(0, 10)
    setVendorsPopup({
      event,
      title: event.title,
      loading: true,
      error: '',
      marketTitle: null,
      vendors: [],
    })
    try {
      const result = await fetchPublicMarketVendors(date)
      setVendorsPopup((current) =>
        current && current.event.id === event.id
          ? {
              ...current,
              loading: false,
              marketTitle: result.market?.title ?? null,
              vendors: result.vendors,
              error: result.market
                ? ''
                : 'No published market day is linked for this date yet.',
            }
          : current,
      )
    } catch (err) {
      setVendorsPopup((current) =>
        current && current.event.id === event.id
          ? {
              ...current,
              loading: false,
              error: err instanceof ApiClientError ? err.message : 'Could not load vendors.',
            }
          : current,
      )
    }
  }

  return (
    <section className={styles.calendar} aria-label="Market calendar">
      <div className={styles.board}>
        <div className={styles.header}>
          <div className={styles.titles}>
            <p className={styles.eyebrow}>What&apos;s on</p>
            <h2 className={styles.monthTitle}>{monthLabel(cursor.year, cursor.month)}</h2>
          </div>
          <div className={styles.nav}>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.legend} aria-hidden="true">
          <span className={styles.legendItem}>
            <span className={styles.dotMarket} /> Market day
          </span>
          <span className={styles.legendItem}>
            <span className={styles.dotTown} /> Town event
          </span>
          <span className={styles.legendItem}>
            <span className={styles.dotOther} /> Other
          </span>
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}
        {loading && !events.length ? <p className={styles.empty}>Loading calendar…</p> : null}

        <div
          className={styles.grid}
          role="grid"
          aria-label={`${monthLabel(cursor.year, cursor.month)} calendar`}
        >
          {WEEKDAYS.map((day) => (
            <div key={day.label} className={styles.weekday} role="columnheader" aria-label={day.label}>
              <span className={styles.weekdayFull}>{day.label.slice(0, 3)}</span>
              <span className={styles.weekdayShort}>{day.short}</span>
            </div>
          ))}
          {cells.map((cell) => {
            const dayEvents = eventsByDay.get(cell.iso) ?? []
            const count = dayEvents.length
            const hasEvents = count > 0
            const hasMultiple = count > 1
            const visibleDots = dayEvents.slice(0, MAX_VISIBLE_DOTS)
            const hiddenCount = Math.max(0, count - visibleDots.length)
            const classNames = [
              styles.day,
              cell.inMonth ? '' : styles.dayOutside,
              cell.iso === todayIso ? styles.dayToday : '',
              hasEvents ? styles.dayHasEvents : '',
              hasMultiple ? styles.dayMulti : '',
              selectedIso === cell.iso ? styles.daySelected : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <button
                key={cell.iso}
                type="button"
                className={classNames}
                role="gridcell"
                aria-label={
                  hasEvents
                    ? `${cell.iso}, ${count} event${count === 1 ? '' : 's'}`
                    : cell.iso
                }
                aria-pressed={selectedIso === cell.iso}
                disabled={!hasEvents}
                onClick={() => setSelectedIso(cell.iso)}
              >
                <span className={styles.dayNum}>{cell.date.getDate()}</span>
                {hasEvents ? (
                  <span className={styles.markers}>
                    {visibleDots.map((dayEvent) => (
                      <span
                        key={dayEvent.id}
                        className={[styles.marker, typeClass(dayEvent.event_type)].join(' ')}
                        title={dayEvent.title}
                      />
                    ))}
                    {hiddenCount > 0 ? (
                      <span className={styles.moreCount} aria-hidden="true">
                        +{hiddenCount}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>

        <div className={styles.details}>
          <div className={styles.detailsHeading}>
            <p className={styles.detailsTitle}>
              {selectedIso ? formatDayHeading(selectedIso) : 'Pick a highlighted day'}
            </p>
            {selectedEvents.length > 0 ? (
              <p className={styles.detailsCount}>
                {selectedEvents.length} event{selectedEvents.length === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>
          {!selectedIso ? (
            <p className={styles.empty}>
              Days with events show coloured dots. Multiple events on one day show a count.
            </p>
          ) : null}
          {selectedEvents.length > 0 ? (
            <ol className={styles.eventList}>
              {selectedEvents.map((event, index) => {
                const start = formatTime(event.start_time)
                const end = formatTime(event.end_time)
                const timeLabel = start && end ? `${start}–${end}` : start || end
                return (
                  <li
                    key={event.id}
                    className={[
                      styles.eventCard,
                      event.event_type === 'market'
                        ? styles.eventCardMarket
                        : event.event_type === 'other'
                          ? styles.eventCardOther
                          : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={styles.eventCardTop}>
                      <span className={styles.eventIndex} aria-hidden="true">
                        {index + 1}
                      </span>
                      <span className={[styles.eventTypePill, typeClass(event.event_type)].join(' ')}>
                        {typeLabel(event.event_type)}
                      </span>
                    </div>
                    <p className={styles.eventTitle}>{event.title}</p>
                    <p className={styles.eventMeta}>
                      {timeLabel ? timeLabel : 'Time TBC'}
                      {event.location ? ` · ${event.location}` : ''}
                    </p>
                    {event.description ? <p className={styles.eventMeta}>{event.description}</p> : null}
                    {event.event_type === 'market' ? (
                      <button
                        type="button"
                        className={styles.vendorsBtn}
                        onClick={() => void openVendorsPopup(event)}
                      >
                        View vendors
                      </button>
                    ) : null}
                  </li>
                )
              })}
            </ol>
          ) : null}
        </div>
      </div>

      {vendorsPopup ? (
        <div
          className={styles.popupBackdrop}
          role="presentation"
          onClick={() => setVendorsPopup(null)}
        >
          <div
            className={styles.popup}
            role="dialog"
            aria-modal="true"
            aria-labelledby="market-vendors-title"
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <div className={styles.popupHeader}>
              <div>
                <p className={styles.popupEyebrow}>Approved stalls</p>
                <h3 id="market-vendors-title" className={styles.popupTitle}>
                  {vendorsPopup.marketTitle || vendorsPopup.title}
                </h3>
                <p className={styles.popupMeta}>
                  {formatDayHeading(vendorsPopup.event.event_date.slice(0, 10))}
                </p>
              </div>
              <button
                type="button"
                className={styles.popupClose}
                aria-label="Close vendors list"
                onClick={() => setVendorsPopup(null)}
              >
                ×
              </button>
            </div>

            {vendorsPopup.loading ? <p className={styles.empty}>Loading vendors…</p> : null}
            {vendorsPopup.error ? <p className={styles.error}>{vendorsPopup.error}</p> : null}
            {!vendorsPopup.loading && !vendorsPopup.error && vendorsPopup.vendors.length === 0 ? (
              <p className={styles.empty}>No approved vendors listed for this market day yet.</p>
            ) : null}
            {!vendorsPopup.loading && vendorsPopup.vendors.length > 0 ? (
              <ul className={styles.vendorList}>
                {vendorsPopup.vendors.map((vendor) => (
                  <li key={vendor.id} className={styles.vendorCard}>
                    <p className={styles.vendorName}>
                      {vendor.business_name || vendor.applicant_name}
                    </p>
                    {vendor.business_name &&
                    vendor.applicant_name &&
                    vendor.business_name !== vendor.applicant_name ? (
                      <p className={styles.vendorBy}>{vendor.applicant_name}</p>
                    ) : null}
                    {vendor.items_for_sale ? (
                      <p className={styles.vendorProducts}>{vendor.items_for_sale}</p>
                    ) : (
                      <p className={styles.vendorBy}>Products to be announced</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}
